/**
 * Assets module data layer — every asset-related API call lives here so pages
 * stay presentational and future features (maintenance, licences, audits…)
 * plug into one place.
 */
import axios from 'axios';
import { apiPath } from '../../config/apiBase';

export const API = apiPath('/api');

export function authHeaders(extra = {}) {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}`, ...extra } };
}

// ---- inventory ----
export const listAssets = (params) =>
  axios.get(`${API}/assets`, { ...authHeaders(), params }).then((r) => r.data);

export const getAssetAllocationReport = (params) =>
  axios.get(`${API}/assets/allocation-report`, { ...authHeaders(), params }).then((r) => r.data);

export const getAssetStats = () =>
  axios.get(`${API}/assets/stats`, authHeaders()).then((r) => r.data);

export const getAsset = (assetId) =>
  axios.get(`${API}/assets/${assetId}`, authHeaders()).then((r) => r.data);

export const createAsset = (payload) =>
  axios.post(`${API}/assets`, payload, authHeaders()).then((r) => r.data);

export const updateAsset = (assetId, payload) =>
  axios.patch(`${API}/assets/${assetId}`, payload, authHeaders()).then((r) => r.data);

export const deleteAsset = (assetId) =>
  axios.delete(`${API}/assets/${assetId}`, authHeaders()).then((r) => r.data);

export const assignAsset = (assetId, payload) =>
  axios.post(`${API}/assets/${assetId}/assign`, payload, authHeaders()).then((r) => r.data);

export const unassignAsset = (assetId) =>
  axios.post(`${API}/assets/${assetId}/unassign`, {}, authHeaders()).then((r) => r.data);

export const setAssetClient = (assetId, clientId) =>
  axios.patch(`${API}/assets/${assetId}/client`, { assigned_client_id: clientId || null }, authHeaders()).then((r) => r.data);

// ---- import / export ----
export async function importAssetsFile(file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axios.post(`${API}/assets/import`, fd,
    authHeaders({ 'Content-Type': 'multipart/form-data' }));
  return res.data;
}

export async function downloadAssetsFile(path, filename) {
  const res = await axios.get(`${API}${path}`, { ...authHeaders(), responseType: 'blob' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(new Blob([res.data]));
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export async function downloadRdpFile(assetId, filename) {
  return downloadAssetsFile(
    `/assets/${assetId}/rdp-file`,
    filename || 'asset.rdp',
  );
}

export async function downloadClientInfraTemplate(clientId, filename) {
  return downloadAssetsFile(
    `/assets/clients/${clientId}/infrastructure/template`,
    filename || 'client_infrastructure_template.xlsx',
  );
}

export async function exportClientInfra(clientId, filename) {
  return downloadAssetsFile(
    `/assets/clients/${clientId}/infrastructure/export`,
    filename || 'client_infrastructure_export.xlsx',
  );
}

export async function importClientInfraFile(clientId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await axios.post(
    `${API}/assets/clients/${clientId}/infrastructure/import`,
    fd,
    authHeaders({ 'Content-Type': 'multipart/form-data' }),
  );
  return res.data;
}

// ---- products & teams ----
export const listAssetTeams = () =>
  axios.get(`${API}/assets/teams`, authHeaders()).then((r) => r.data);

export const getAssetTeam = (teamId) =>
  axios.get(`${API}/assets/teams/${teamId}`, authHeaders()).then((r) => r.data);

export const getUserAssets = (userId) =>
  axios.get(`${API}/assets/user/${userId}`, authHeaders()).then((r) => r.data);

export const getUserClients = (userId) =>
  axios.get(`${API}/assets/user/${userId}/clients`, authHeaders()).then((r) => r.data);

export const assignProductCompany = (productId, companyId) =>
  axios.patch(`${API}/products/${productId}`, { company_id: companyId }, authHeaders()).then((r) => r.data);

// ---- clients (per-product client accounts) ----
export const listProductClients = (productId) =>
  axios.get(`${API}/products/${productId}/clients`, authHeaders()).then((r) => r.data);

export const createProductClient = (productId, payload) =>
  axios.post(`${API}/products/${productId}/clients`, payload, authHeaders()).then((r) => r.data);

export const updateProductClient = (clientId, payload) =>
  axios.patch(`${API}/products/clients/${clientId}`, payload, authHeaders()).then((r) => r.data);

export const updateClientBilling = (clientId, payload) =>
  axios.patch(`${API}/products/clients/${clientId}/billing`, payload, authHeaders()).then((r) => r.data);

export const getClientEconomics = (clientId) =>
  axios.get(`${API}/assets/clients/${clientId}/economics`, authHeaders()).then((r) => r.data);

export const getEconomicsProductsDetail = () =>
  axios.get(`${API}/assets/economics/detail/products`, authHeaders()).then((r) => r.data);

export const getEconomicsProductDetail = (productId) =>
  axios.get(`${API}/assets/economics/detail/products/${encodeURIComponent(productId)}`, authHeaders()).then((r) => r.data);

export const getEconomicsClientsDetail = () =>
  axios.get(`${API}/assets/economics/detail/clients`, authHeaders()).then((r) => r.data);

export const getEconomicsSpendMixDetail = (category, productId) =>
  axios.get(`${API}/assets/economics/detail/spend-mix`, {
    ...authHeaders(),
    params: {
      ...(category ? { category } : {}),
      ...(productId ? { product_id: productId } : {}),
    },
  }).then((r) => r.data);

export const updateUserMonthlyCost = (userId, monthlyCost) =>
  axios.patch(
    `${API}/assets/user/${userId}/monthly-cost`,
    { monthly_cost: monthlyCost },
    authHeaders(),
  ).then((r) => r.data);

export const deleteProductClient = (clientId) =>
  axios.delete(`${API}/products/clients/${clientId}`, authHeaders()).then((r) => r.data);

export const listImportableClientSources = (productId) =>
  axios.get(`${API}/products/${productId}/clients/import-sources`, authHeaders()).then((r) => r.data);

export const importClientsFromDataSource = (productId, dataSourceId) =>
  axios.post(`${API}/products/${productId}/clients/import-from-data-source`,
    { data_source_id: dataSourceId }, authHeaders()).then((r) => r.data);

export const getClientDataSourceLink = (productId) =>
  axios.get(`${API}/products/${productId}/clients/data-source-link`, authHeaders()).then((r) => r.data);

export const unlinkClientDataSource = (productId) =>
  axios.delete(`${API}/products/${productId}/clients/data-source-link`, authHeaders()).then((r) => r.data);

export const updateTeamMemberClients = (productId, userId, clientIds) =>
  axios.patch(`${API}/products/${productId}/team/members/${userId}/client`, { client_ids: clientIds }, authHeaders()).then((r) => r.data);

// ---- companies ----
export const listCompanies = () =>
  axios.get(`${API}/companies`, authHeaders()).then((r) => r.data);

export const getCompaniesSummary = () =>
  axios.get(`${API}/assets/companies/summary`, authHeaders()).then((r) => r.data);

export const listCompanyEmployees = (companyId) =>
  axios.get(`${API}/assets/companies/${encodeURIComponent(companyId)}/employees`, authHeaders()).then((r) => r.data);

export const listCompanyAssets = (companyId) =>
  axios.get(`${API}/assets/companies/${encodeURIComponent(companyId)}/assets`, authHeaders()).then((r) => r.data);
